/**
 * Anthropic Provider Implementation
 * 
 * Implements the IAIProvider interface for Anthropic's Claude API.
 * Supports models like Claude Opus 4.5, Claude Sonnet 4.5, Claude Haiku 4.5.
 * 
 * Key differences from OpenAI:
 * - Uses x-api-key header instead of Bearer token
 * - System message is passed as a separate parameter, not in messages array
 * - Requires anthropic-version header
 * - Different response format
 */

import {
  AIProvider,
  AIModelConfig,
  AIMessage,
  AIRequestOptions,
  AIResponse
} from '../AIProvider.types';
import { BaseAIProvider } from '../BaseAIProvider';
import { ANTHROPIC_MODELS } from '../config/modelConfig';
import { PROVIDER_ENDPOINTS, ANTHROPIC_CONFIG } from '../config/providerConfig';
import { ApiClient } from '../../ApiClient';

/**
 * Anthropic API response format
 */
interface AnthropicResponse {
  id: string;
  type: string;
  role: string;
  content: Array<{
    type: 'text';
    text: string;
  }>;
  model: string;
  stop_reason: string | null;
  stop_sequence: string | null;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

/**
 * Anthropic message format
 * Note: Anthropic does not support system role in messages array
 */
interface AnthropicMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Anthropic request payload format
 */
interface AnthropicRequestPayload {
  model: string;
  max_tokens: number;
  messages: AnthropicMessage[];
  system?: string;
  temperature?: number;
  metadata?: {
    user_id?: string;
  };
  output_format?: {
    type: 'json_schema';
    schema: Record<string, unknown>;
  };
}

/**
 * Anthropic Provider
 * 
 * Handles all communication with Anthropic's API, including:
 * - Chat completions with Claude models
 * - JSON mode via tool use
 * - Token management
 */
export class AnthropicProvider extends BaseAIProvider {
  readonly provider = AIProvider.ANTHROPIC;
  
  /**
   * Get the Anthropic API key from Forge
   */
  protected async getAuthToken(): Promise<string> {
    return await this.invokeResolver<string>('getAnthropicApiToken');
  }
  
  /**
   * Generate a completion using Anthropic's API
   */
  async generateCompletion(
    messages: AIMessage[],
    options: AIRequestOptions
  ): Promise<AIResponse> {
    this.validateRequestOptions(options);
    // Anthropic has different temperature range (0-1 instead of 0-2)
    this.validateAnthropicTemperature(options);
    
    try {
      const apiKey = await this.getAuthToken();
      
      const payload = this.buildRequestPayload(messages, options);
      
      const headers: Record<string, string> = {
        'x-api-key': apiKey,
        [ANTHROPIC_CONFIG.apiVersionHeader]: ANTHROPIC_CONFIG.apiVersion,
        'Content-Type': 'application/json'
      };

      if (options.jsonMode) {
        headers[ANTHROPIC_CONFIG.structuredOutputsHeader] =
          ANTHROPIC_CONFIG.structuredOutputsBeta;
      }
      
      const response = await ApiClient.post(
        PROVIDER_ENDPOINTS[AIProvider.ANTHROPIC],
        payload,
        headers
      ) as AnthropicResponse;
      
      return this.normalizeResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }
  
  /**
   * Validate Anthropic-specific temperature range
   * Anthropic uses 0-1 range instead of 0-2
   */
  private validateAnthropicTemperature(options: AIRequestOptions): void {
    if (options.temperature !== undefined && options.temperature > 1) {
      // Clamp to Anthropic's max of 1
      options.temperature = Math.min(options.temperature, 1);
    }
  }
  
  /**
   * Build the request payload for Anthropic's API
   */
  private buildRequestPayload(
    messages: AIMessage[],
    options: AIRequestOptions
  ): AnthropicRequestPayload {
    const { formattedMessages, systemPrompt } = this.formatMessages(messages, options.systemPrompt);
    
    const payload: AnthropicRequestPayload = {
      model: this.getApiModelName(options.model),
      max_tokens: options.maxTokens ?? ANTHROPIC_CONFIG.defaultMaxTokens,
      messages: formattedMessages
    };
    
    // Anthropic requires system prompt as a separate parameter
    if (systemPrompt) {
      payload.system = systemPrompt;
    }
    
    // Add temperature if specified (after validation)
    if (options.temperature !== undefined) {
      payload.temperature = options.temperature;
    }

    if (options.jsonMode) {
      payload.output_format = {
        type: 'json_schema',
        schema: {
          type: 'object',
          additionalProperties: true
        }
      };
    }
    
    return payload;
  }
  
  /**
   * Get the API model name from the model ID
   * Anthropic uses full model names with dates
   */
  private getApiModelName(modelId: string): string {
    const model = ANTHROPIC_MODELS.find(m => m.id === modelId);
    return model?.name || modelId;
  }
  
  /**
   * Format messages for Anthropic's API
   * Anthropic has specific requirements:
   * - System messages must be passed as a separate parameter
   * - Only 'user' and 'assistant' roles are allowed in messages array
   * - Messages must alternate between user and assistant
   */
  private formatMessages(
    messages: AIMessage[],
    systemPrompt?: string
  ): { formattedMessages: AnthropicMessage[]; systemPrompt?: string } {
    let extractedSystemPrompt = systemPrompt || '';
    const formatted: AnthropicMessage[] = [];
    
    // Process messages, extracting any system messages
    for (const msg of this.normalizeMessages(messages)) {
      if (msg.role === 'system') {
        // Append system messages to the system prompt
        extractedSystemPrompt = extractedSystemPrompt 
          ? `${extractedSystemPrompt}\n\n${msg.content}`
          : msg.content;
      } else {
        // Add user and assistant messages
        formatted.push({
          role: msg.role as 'user' | 'assistant',
          content: msg.content
        });
      }
    }
    
    // Anthropic requires the first message to be from user
    // If we don't have any messages, add an empty user message
    if (formatted.length === 0) {
      formatted.push({
        role: 'user',
        content: 'Please provide a response.'
      });
    }
    
    // Ensure first message is from user
    if (formatted[0].role !== 'user') {
      formatted.unshift({
        role: 'user',
        content: 'Please continue.'
      });
    }
    
    return {
      formattedMessages: formatted,
      systemPrompt: extractedSystemPrompt || undefined
    };
  }
  
  /**
   * Normalize Anthropic's response to our standard format
   */
  private normalizeResponse(response: AnthropicResponse): AIResponse {
    const contentBlocks = Array.isArray(response?.content) ? response.content : null;
    if (!contentBlocks) {
      const errorMessage = (response as unknown as { error?: { message?: string } })?.error?.message;
      throw new Error(errorMessage || 'Anthropic response missing content');
    }

    const textContent = contentBlocks
      .filter(block => block.type === 'text')
      .map(block => block.text)
      .join('');
    
    return {
      content: textContent,
      model: response.model,
      provider: this.provider,
      usage: {
        promptTokens: response.usage?.input_tokens || 0,
        completionTokens: response.usage?.output_tokens || 0,
        totalTokens: (response.usage?.input_tokens || 0) + (response.usage?.output_tokens || 0)
      },
      finishReason: this.mapStopReason(response.stop_reason)
    };
  }
  
  /**
   * Map Anthropic's stop_reason to our standard finish_reason
   */
  private mapStopReason(stopReason: string | null): string {
    switch (stopReason) {
      case 'end_turn':
        return 'stop';
      case 'max_tokens':
        return 'length';
      case 'stop_sequence':
        return 'stop';
      case 'tool_use':
        return 'tool_calls';
      default:
        return stopReason || 'stop';
    }
  }
  
  /**
   * Check if Anthropic provider is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      const apiKey = await this.getAuthToken();
      return !!apiKey && apiKey.length > 0;
    } catch {
      return false;
    }
  }
  
  /**
   * Get available Anthropic models
   */
  getAvailableModels(): AIModelConfig[] {
    return ANTHROPIC_MODELS;
  }
}
